import { useQuery } from '@tanstack/react-query';
import { Search, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import fallbackBottle from '@/assets/images/Mockup-Golden.png';
import { productListOptions } from '@/features/storefront/hooks/useCatalog';
import { cn } from '@/shared/utils/cn';
import { formatPKR } from '@/shared/utils/format';

/**
 * Navbar search: the icon expands into an input with a live suggestions dropdown
 * (thumbnail + name + price). Submitting sends the shopper to the collection
 * filtered by `?q=`; the backend does the matching (name/notes).
 */
export function NavSearch({ iconClass }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState(() => params.get('q') ?? '');
  const [debounced, setDebounced] = useState('');
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  // Debounce keystrokes so we don't hit the API on every letter.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(term.trim()), 220);
    return () => clearTimeout(id);
  }, [term]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = open && debounced.length >= 2;
  const { data, isFetching } = useQuery({
    ...productListOptions({ q: debounced, limit: 6 }),
    enabled: active,
  });
  const suggestions = active ? (data?.items ?? []) : [];

  function goToCollection() {
    const q = term.trim();
    if (!q) return;
    navigate(`${routes.collection}?q=${encodeURIComponent(q)}`);
    setOpen(false);
  }

  function pick(slug) {
    navigate(routes.product(slug));
    setOpen(false);
    setTerm('');
  }

  return (
    <div ref={rootRef} className="relative flex items-center">
      {open && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            goToCollection();
          }}
          className="absolute top-1/2 right-0 flex -translate-y-1/2 items-center"
          role="search"
        >
          <input
            ref={inputRef}
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search scents…"
            aria-label="Search products"
            className="w-52 rounded-full border border-[color:var(--color-line)] bg-white py-2 pr-9 pl-4 text-sm text-[color:var(--color-ink)] shadow-sm outline-none focus:border-gold-500 sm:w-72"
          />
          <button
            type="submit"
            aria-label="Search"
            className="absolute right-1 inline-flex size-8 items-center justify-center rounded-full text-[color:var(--color-body)] hover:text-gold-700"
          >
            <Search size={16} strokeWidth={1.7} />
          </button>

          {/* Suggestions dropdown */}
          {active && (
            <div className="absolute top-full right-0 mt-2 w-72 overflow-hidden rounded-2xl border border-[color:var(--color-line)] bg-white shadow-[var(--shadow-lift)] sm:w-80">
              {suggestions.length > 0 ? (
                <>
                  <ul className="max-h-[60vh] overflow-y-auto py-1.5">
                    {suggestions.map((p) => (
                      <li key={p.slug}>
                        <button
                          type="button"
                          onClick={() => pick(p.slug)}
                          className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-gold-50"
                        >
                          <span className="size-12 shrink-0 overflow-hidden rounded-lg bg-[color:var(--color-section)]">
                            <img
                              src={p.image || fallbackBottle}
                              alt=""
                              className="h-full w-full object-contain p-1"
                            />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-serif text-sm text-[color:var(--color-ink)]">
                              {p.name}
                            </span>
                            <span className="block truncate text-xs text-[color:var(--color-muted)]">
                              {p.shortNotes}
                            </span>
                          </span>
                          <span className="shrink-0 text-sm font-medium text-[color:var(--color-ink)]">
                            {formatPKR(p.price)}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={goToCollection}
                    className="block w-full border-t border-[color:var(--color-line)] px-4 py-2.5 text-center text-xs font-semibold text-gold-700 hover:bg-gold-50"
                  >
                    See all results for “{debounced}”
                  </button>
                </>
              ) : (
                <p className="px-4 py-6 text-center text-sm text-[color:var(--color-muted)]">
                  {isFetching ? 'Searching…' : `No scents match “${debounced}”`}
                </p>
              )}
            </div>
          )}
        </form>
      )}
      <button
        type="button"
        aria-label={open ? 'Close search' : 'Search'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(iconClass, open && 'pointer-events-none opacity-0')}
      >
        {open ? <X size={19} strokeWidth={1.6} /> : <Search size={19} strokeWidth={1.6} />}
      </button>
    </div>
  );
}
